// @section 1 Atlas Calls — general-purpose dialer screen.
// List picker → per-list queue with prior email touches → click-to-dial via
// Google Voice deep link (routed to the signed-in operator's `authuser` email)
// → disposition row writes back to serving.cold_call_attempts.
//
// Data: /api/hq/calls/lists, /api/hq/calls/lists/{listId}/queue, POST /attempts.
// Voice URL: voice.google.com/u/0/calls?a=nc,%2B<E.164>&authuser=<me.email>.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CallAttempt,
  CallDisposition,
  CallItemDetail,
  CallListRow,
  CallQueue,
  CallQueueItem,
  Me,
  SendCallEmailResult,
  getCallItemDetail,
  getCallQueue,
  getMe,
  listCallLists,
  logCallAttempt,
  sendCallEmail,
} from '../api';
import { useIsMobile } from '../lib/useIsMobile';

type FilterKey = 'today' | 'all' | 'unattempted' | 'callback_due';

const DISPOSITIONS: { key: CallDisposition; label: string }[] = [
  { key: 'connected',    label: 'Connected' },
  { key: 'voicemail',    label: 'Voicemail' },
  { key: 'no_answer',    label: 'No answer' },
  { key: 'bad_number',   label: 'Bad number' },
  { key: 'wrong_person', label: 'Wrong person' },
];

// @block 1.1 Phone helpers
// Normalize a free-text phone to E.164 (+1NNNNNNNNNN for US). Returns null if it
// can't be confidently normalized — those rows render as "no phone" and skip the
// Voice button rather than gambling on a bad number.
function toE164(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const digits = raw.replace(/\D+/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (digits.length >= 11 && digits.length <= 15 && raw.trim().startsWith('+')) return `+${digits}`;
  return null;
}

function formatPhone(raw: string | null | undefined): string {
  const e164 = toE164(raw);
  if (!e164) return raw ?? '';
  // +1 NNN NNN NNNN
  if (e164.startsWith('+1') && e164.length === 12) {
    return `(${e164.slice(2, 5)}) ${e164.slice(5, 8)}-${e164.slice(8)}`;
  }
  return e164;
}

// @block 1.2 Build the Google Voice deep link. authuser routes to the user's
// Workspace identity if they're signed in to it; otherwise Voice prompts a sign-in.
function buildVoiceUrl(phoneE164: string, callerEmail?: string | null): string {
  const base = 'https://voice.google.com/u/0/calls';
  const params = new URLSearchParams();
  // The `a=nc,...` segment pre-populates the dialer.
  params.set('a', `nc,${phoneE164}`);
  if (callerEmail) params.set('authuser', callerEmail);
  return `${base}?${params.toString()}`;
}

// @block 1.3 Date helpers (local-time, short)
function fmtAt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso ?? '—';
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function fullName(item: CallQueueItem): string {
  const parts = [item.first_name, item.last_name].filter(Boolean);
  if (parts.length === 0) return item.email.split('@')[0];
  return parts.join(' ');
}

// @block 1.4 Disposition pill style
function dispClass(d: CallDisposition | null | undefined): string {
  switch (d) {
    case 'connected':    return 'pill pill-good';
    case 'voicemail':    return 'pill pill-warn';
    case 'no_answer':    return 'pill pill-neutral';
    case 'bad_number':   return 'pill pill-bad';
    case 'wrong_person': return 'pill pill-bad';
    case 'dialed':       return 'pill pill-neutral';
    default:             return 'pill pill-muted';
  }
}

// @section 2 Component
export default function Calls() {
  const [me, setMe] = useState<Me | null>(null);
  const [lists, setLists] = useState<CallListRow[] | null>(null);
  const [activeListId, setActiveListId] = useState<string | null>(null);
  const [queue, setQueue] = useState<CallQueue | null>(null);
  const [filter, setFilter] = useState<FilterKey>('today');
  const [error, setError] = useState<string | null>(null);
  const [busyItem, setBusyItem] = useState<number | null>(null);
  // Optimistic last-disposition overrides keyed by item_id — keep the UI fresh
  // without a full queue refetch on every disposition save.
  const [pendingDisp, setPendingDisp] = useState<Record<number, Partial<CallAttempt>>>({});
  const [openDispFor, setOpenDispFor] = useState<number | null>(null);
  // Items completed this session (call logged) — collapsed out of the active flow.
  const [handled, setHandled] = useState<Set<number>>(new Set());
  // @block 2.1 Detail panel state — keyed by item_id. Lazy-loaded on first expand.
  const [openDetailFor, setOpenDetailFor] = useState<number | null>(null);
  const [details, setDetails] = useState<Record<number, CallItemDetail>>({});
  const [detailLoading, setDetailLoading] = useState<number | null>(null);
  const [sendBusy, setSendBusy] = useState<number | null>(null);
  // Mobile fork: the queue row is a 6-col grid + the detail panel is 2-col, both
  // of which crush on a phone. On mobile we stack them to a single column.
  const isMobile = useIsMobile();

  // @block 2.2 Bootstrap — fetch me + lists in parallel
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [meRes, listsRes] = await Promise.all([getMe(), listCallLists()]);
        if (!alive) return;
        setMe(meRes);
        setLists(listsRes.lists);
        if (listsRes.lists.length > 0 && !activeListId) {
          setActiveListId(listsRes.lists[0].list_id);
        }
      } catch (e) {
        if (alive) setError(String(e));
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // @block 2.3 Load queue when list or filter changes
  const refreshQueue = useCallback(async () => {
    if (!activeListId) return;
    try {
      // 'today' is a client-side view (callbacks-due then unattempted, in order);
      // fetch the full queue and order it below in displayItems.
      const q = await getCallQueue(activeListId, filter === 'today' ? 'all' : filter);
      setQueue(q);
      setPendingDisp({});
    } catch (e) { setError(String(e)); }
  }, [activeListId, filter]);

  useEffect(() => { void refreshQueue(); }, [refreshQueue]);

  // @block 2.4 Click "Call"
  // 1. Mobile: dial via the phone's native dialer (tel:) — always works, calls
  //    from the cell. Desktop: open Google Voice web (authuser=me.email) in a tab.
  //    (Routing mobile through the Google Voice business number is a later item.)
  // 2. Optimistically log a 'dialed' attempt (so the row reflects today's outreach
  //    even if Adam never circles back to set a final disposition).
  // 3. Reveal the disposition row so the operator can pick an outcome.
  const onCall = useCallback(async (item: CallQueueItem) => {
    if (!activeListId) return;
    const e164 = toE164(item.phone);
    if (!e164) { setError(`No valid phone for ${fullName(item)}`); return; }
    const url = isMobile ? `tel:${e164}` : buildVoiceUrl(e164, me?.email);
    if (isMobile) {
      window.location.href = url;                        // native phone dialer
    } else {
      window.open(url, '_blank', 'noopener,noreferrer'); // Google Voice web
    }
    setOpenDispFor(item.item_id);
    setBusyItem(item.item_id);
    try {
      const attempt = await logCallAttempt({
        itemId: item.item_id,
        listId: activeListId,
        disposition: 'dialed',
        voiceUrl: url,
      });
      setPendingDisp(prev => ({ ...prev, [item.item_id]: attempt }));
    } catch (e) { setError(String(e)); }
    finally { setBusyItem(null); }
  }, [activeListId, me, isMobile]);

  // @block 2.5 Submit disposition row
  const onLogDisposition = useCallback(async (
    item: CallQueueItem,
    disposition: CallDisposition,
    notes: string,
    callbackAt: string,
  ) => {
    if (!activeListId) return;
    setBusyItem(item.item_id);
    try {
      const attempt = await logCallAttempt({
        itemId: item.item_id,
        listId: activeListId,
        disposition,
        notes: notes.trim() || null,
        callbackAt: callbackAt ? new Date(callbackAt).toISOString() : null,
      });
      setPendingDisp(prev => ({ ...prev, [item.item_id]: attempt }));
      setOpenDispFor(null);
      // Complete this call: collapse it out of the active flow, then scroll to the
      // next not-yet-handled call so the operator keeps moving down the list.
      setHandled(prev => { const next = new Set(prev); next.add(item.item_id); return next; });
      const items = queue?.items ?? [];
      const idx = items.findIndex(i => i.item_id === item.item_id);
      const nextItem = items.slice(idx + 1).find(i => !handled.has(i.item_id));
      if (nextItem) {
        setTimeout(() => {
          document.getElementById(`call-item-${nextItem.item_id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 60);
      }
    } catch (e) { setError(String(e)); }
    finally { setBusyItem(null); }
  }, [activeListId, queue, handled]);

  // @block 2.6 Toggle the per-row detail panel (talking points / VM script / email draft).
  const onToggleDetail = useCallback(async (item: CallQueueItem) => {
    if (openDetailFor === item.item_id) {
      setOpenDetailFor(null);
      return;
    }
    setOpenDetailFor(item.item_id);
    if (!details[item.item_id]) {
      setDetailLoading(item.item_id);
      try {
        const d = await getCallItemDetail(item.item_id);
        setDetails(prev => ({ ...prev, [item.item_id]: d }));
      } catch (e) { setError(String(e)); }
      finally { setDetailLoading(null); }
    }
  }, [openDetailFor, details]);

  // @block 2.7 Send the pre-drafted touch-1 email via Bridge Workspace SA
  // (from = adam_caccamise@signal-rev.com). On success: refresh detail to show sent_at.
  const onSendEmail = useCallback(async (item: CallQueueItem) => {
    const cached = details[item.item_id];
    if (!cached?.email_draft || cached.email_draft.sent_at) return;
    if (!window.confirm(
      `Send the touch-1 email to ${cached.email}?\n\n` +
      `Subject: ${cached.email_draft.subject}\n` +
      `From: ${cached.email_draft.sender_account}\n\n` +
      `Click OK to send now.`,
    )) return;
    setSendBusy(item.item_id);
    try {
      const r: SendCallEmailResult = await sendCallEmail(item.item_id);
      // Mutate the cached draft's sent_at so the UI flips to "sent".
      setDetails(prev => ({
        ...prev,
        [item.item_id]: {
          ...prev[item.item_id],
          email_draft: prev[item.item_id].email_draft
            ? { ...prev[item.item_id].email_draft!, sent_at: r.sent_at }
            : null,
        },
      }));
    } catch (e) { setError(String(e)); }
    finally { setSendBusy(null); }
  }, [details]);

  // @block 2.8 Effective last-attempt = optimistic override OR server value
  const effective = useCallback((item: CallQueueItem) => {
    const o = pendingDisp[item.item_id];
    return {
      disposition: (o?.disposition ?? item.last_disposition) as CallDisposition | null,
      called_at: o?.called_at ?? item.last_attempt_at,
      callback_at: o?.callback_at ?? item.last_callback_at,
      called_by: o?.called_by_email ?? item.last_called_by,
      attempts_total: (item.attempts_total ?? 0) + (o ? 1 : 0),
    };
  }, [pendingDisp]);

  // @block 2.9 Header counters
  const counters = useMemo(() => {
    if (!queue) return { total: 0, attempted: 0, dueCallback: 0 };
    const now = new Date();
    let attempted = 0, due = 0;
    for (const it of queue.items) {
      const e = effective(it);
      if (e.attempts_total > 0) attempted++;
      if (e.callback_at && new Date(e.callback_at) <= now) due++;
    }
    return { total: queue.items.length, attempted, dueCallback: due };
  }, [queue, effective]);

  // @block 2.10 "Today" view ordering: callbacks you owe (due) first, then
  // never-called, then already-worked — each tier in rank order. Other filters
  // keep the server order. This is the "who do I call today, in order" list.
  const displayItems = useMemo(() => {
    const items = queue?.items ?? [];
    if (filter !== 'today') return items;
    const now = Date.now();
    const tier = (it: CallQueueItem) => {
      const e = effective(it);
      const cb = e.callback_at ? new Date(e.callback_at).getTime() : null;
      if (cb && cb <= now) return 0;                  // callback due — call back first
      if ((e.attempts_total ?? 0) === 0) return 1;    // never called — fresh
      return 2;                                        // already worked, no due callback
    };
    return [...items].sort((a, b) => tier(a) - tier(b) || a.rank - b.rank);
  }, [queue, filter, effective]);

  return (
    <div style={{
      padding: isMobile ? '14px 14px' : '20px 24px', color: 'var(--v5-text, #e6e8eb)',
      ...(isMobile ? { height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch' } : null),
    }}>
      <header style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Calls</h1>
        {me && !isMobile && (
          <span style={{ fontSize: 12, opacity: 0.6 }}>
            Dialing as <code>{me.email}</code> via Google Voice
          </span>
        )}
      </header>

      {error && (
        <div style={{ marginBottom: 12, padding: '8px 12px', background: '#3a1414',
                      border: '1px solid #6e2222', borderRadius: 6, fontSize: 13 }}>
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: 12, background: 'transparent',
                  color: '#fff', border: '1px solid #6e2222', borderRadius: 4, padding: '2px 8px',
                  cursor: 'pointer' }}>dismiss</button>
        </div>
      )}

      {/* @block 2.7 List picker + filter chips */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12, opacity: 0.7 }}>List:</label>
        <select
          value={activeListId ?? ''}
          onChange={(e) => setActiveListId(e.target.value || null)}
          style={{ background: '#16191e', color: '#e6e8eb', border: '1px solid #2a2f37',
                  borderRadius: 6, padding: '6px 10px',
                  minWidth: isMobile ? 0 : 320, width: isMobile ? '100%' : undefined }}
        >
          {lists?.map(l => (
            <option key={l.list_id} value={l.list_id}>
              {l.list_name} — {l.total_items} ({l.attempted_items} attempted)
            </option>
          ))}
          {lists && lists.length === 0 && <option value="">No lists yet</option>}
        </select>

        <span style={{ width: 1, height: 18, background: '#2a2f37', margin: '0 4px' }} />

        {(['today', 'all', 'unattempted', 'callback_due'] as const).map(k => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            style={{
              background: filter === k ? '#1e3a5f' : 'transparent',
              color: filter === k ? '#a7d3ff' : '#9aa1ad',
              border: `1px solid ${filter === k ? '#2d5288' : '#2a2f37'}`,
              borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer',
            }}
          >
            {k === 'today' ? 'Today' : k === 'all' ? 'All' : k === 'unattempted' ? 'Unattempted' : 'Callback due'}
          </button>
        ))}

        <span style={{ flex: 1 }} />

        {queue && (
          <span style={{ fontSize: 12, opacity: 0.7 }}>
            {counters.total} shown · {counters.attempted} attempted · {counters.dueCallback} callback-due{handled.size > 0 ? ` · ${handled.size} done` : ''}
          </span>
        )}
      </div>

      {/* @block 2.8 Queue table */}
      {!queue && !error && (
        <div style={{ padding: 24, opacity: 0.6 }}>Loading…</div>
      )}

      {queue && queue.items.length === 0 && (
        <div style={{ padding: 24, opacity: 0.6 }}>
          No items match the current filter.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {displayItems.map(item => {
          const eff = effective(item);
          const phone = toE164(item.phone);
          const isOpen = openDispFor === item.item_id;
          const isBusy = busyItem === item.item_id;
          // Completed this session: collapse to a thin done row, out of the flow.
          if (handled.has(item.item_id)) {
            return (
              <div key={item.item_id} id={`call-item-${item.item_id}`}
                   style={{ background: '#14160f', border: '1px solid #26331f', borderRadius: 8,
                            padding: '10px 14px', display: 'flex', alignItems: 'center',
                            justifyContent: 'space-between', gap: 10, opacity: 0.75 }}>
                <span style={{ fontSize: 13, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <span style={{ color: '#a3e4b6', marginRight: 8 }}>✓</span>
                  {item.firm_name ?? item.domain}
                  <span style={{ opacity: 0.6 }}> · {eff.disposition ?? 'done'}</span>
                </span>
                <button
                  onClick={() => setHandled(prev => { const n = new Set(prev); n.delete(item.item_id); return n; })}
                  style={{ flexShrink: 0, background: 'transparent', color: '#9aa1ad', border: '1px solid #2a2f37',
                           borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}
                >
                  Reopen
                </button>
              </div>
            );
          }
          return (
            <div key={item.item_id} id={`call-item-${item.item_id}`}
                 style={{ background: '#16191e', border: '1px solid #242a32',
                          borderRadius: 8, padding: '12px 14px' }}>
              {isMobile ? (
                <CallCardMobile
                  item={item}
                  eff={eff}
                  phone={phone}
                  isBusy={isBusy}
                  detailOpen={openDetailFor === item.item_id}
                  dispOpen={isOpen}
                  onCall={() => void onCall(item)}
                  onToggleDetail={() => void onToggleDetail(item)}
                  onToggleDisp={() => setOpenDispFor(isOpen ? null : item.item_id)}
                />
              ) : (
              <div style={{ display: 'grid',
                            gridTemplateColumns: '40px 1.5fr 1.5fr 1fr 1fr auto',
                            gap: 12, alignItems: 'center' }}>
                <div style={{ fontSize: 11, opacity: 0.5 }}>#{item.rank}</div>

                <div
                  onClick={() => void onToggleDetail(item)}
                  title="Click for talking points, voicemail script, and email pre-draft"
                  style={{ cursor: 'pointer' }}
                >
                  <div style={{
                    fontWeight: 600,
                    color: openDetailFor === item.item_id ? '#a7d3ff' : 'inherit',
                    textDecoration: 'underline',
                    textDecorationColor: '#2a2f37',
                    textUnderlineOffset: 3,
                  }}>
                    {item.firm_name ?? item.domain}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>
                    {[item.city, item.state].filter(Boolean).join(', ')}
                    {item.gmaps_rating != null && (
                      <> · ★ {item.gmaps_rating}
                        {item.gmaps_review_count ? ` (${item.gmaps_review_count})` : ''}
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 13 }}>{fullName(item)}</div>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>
                    {item.title ?? ''} {item.title && item.email ? ' · ' : ''}
                    <a href={`mailto:${item.email}`}
                       style={{ color: '#9aa1ad', textDecoration: 'none' }}>{item.email}</a>
                  </div>
                </div>

                <div style={{ fontSize: 13 }}>
                  {phone ? (
                    <a href={`tel:${phone}`}
                       style={{ color: '#a7d3ff', textDecoration: 'none' }}>
                      {formatPhone(item.phone)}
                    </a>
                  ) : (
                    <span style={{ opacity: 0.4 }}>no phone</span>
                  )}
                  {item.touches.length > 0 && (
                    <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
                      {item.touches.length} email{item.touches.length === 1 ? '' : 's'} sent
                      {item.touches[item.touches.length - 1]?.sent_at && (
                        <> · last {fmtAt(item.touches[item.touches.length - 1]?.sent_at)}</>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <span className={dispClass(eff.disposition)}
                        style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 999,
                          fontSize: 11, border: '1px solid #2a2f37',
                          background:
                            eff.disposition === 'connected'    ? '#143a1f' :
                            eff.disposition === 'voicemail'    ? '#3a2e14' :
                            eff.disposition === 'bad_number'   ? '#3a1414' :
                            eff.disposition === 'wrong_person' ? '#3a1414' :
                            eff.disposition ? '#1f2329' : 'transparent',
                          color:
                            eff.disposition === 'connected'    ? '#a3e4b6' :
                            eff.disposition === 'voicemail'    ? '#e4ce8b' :
                            eff.disposition === 'bad_number'   ? '#e88a8a' :
                            eff.disposition === 'wrong_person' ? '#e88a8a' :
                            eff.disposition ? '#9aa1ad' : '#5a6068',
                        }}>
                    {eff.disposition ?? 'not called'}
                  </span>
                  <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
                    {eff.attempts_total > 0 && (
                      <>
                        {eff.attempts_total} attempt{eff.attempts_total === 1 ? '' : 's'}
                        {eff.called_at && ` · ${fmtAt(eff.called_at)}`}
                      </>
                    )}
                    {eff.callback_at && (
                      <> · callback {fmtAt(eff.callback_at)}</>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {item.html_path && (
                    <a href={item.html_path}
                       target="_blank" rel="noopener noreferrer"
                       style={{ fontSize: 12, color: '#9aa1ad',
                                border: '1px solid #2a2f37', borderRadius: 6,
                                padding: '6px 10px', textDecoration: 'none' }}>
                      Teardown
                    </a>
                  )}
                  <button
                    onClick={() => void onCall(item)}
                    disabled={!phone || isBusy}
                    style={{
                      background: phone ? '#1e3a5f' : '#1f2329',
                      color: phone ? '#a7d3ff' : '#5a6068',
                      border: `1px solid ${phone ? '#2d5288' : '#2a2f37'}`,
                      borderRadius: 6, padding: isMobile ? '10px 16px' : '6px 12px',
                      fontSize: isMobile ? 14 : 12, fontWeight: 600,
                      flex: isMobile ? '1 1 auto' : undefined,
                      cursor: phone ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {isBusy ? '…' : '📞 Call'}
                  </button>
                  <button
                    onClick={() => setOpenDispFor(isOpen ? null : item.item_id)}
                    style={{
                      background: 'transparent', color: '#9aa1ad',
                      border: '1px solid #2a2f37', borderRadius: 6,
                      padding: '6px 10px', fontSize: 12, cursor: 'pointer',
                    }}
                  >
                    {isOpen ? 'Hide' : 'Log'}
                  </button>
                </div>
              </div>
              )}

              {/* @block 2.9 Disposition row (after-call) */}
              {isOpen && (
                <DispositionRow
                  item={item}
                  onSubmit={(d, notes, cb) => void onLogDisposition(item, d, notes, cb)}
                  onCancel={() => setOpenDispFor(null)}
                />
              )}

              {/* @block 2.10 Detail panel (talking points + VM script + email pre-draft) */}
              {openDetailFor === item.item_id && (
                <DetailPanel
                  loading={detailLoading === item.item_id}
                  detail={details[item.item_id] ?? null}
                  sending={sendBusy === item.item_id}
                  onSend={() => void onSendEmail(item)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// @section 3 DetailPanel — talking points, voicemail script, pre-drafted email,
// and a one-click "Send via Signal" button. Revealed when the operator clicks
// the firm-name cell in the queue table. Data: /api/hq/calls/items/{id}/detail.
type DetailProps = {
  loading: boolean;
  detail: CallItemDetail | null;
  sending: boolean;
  onSend: () => void;
};

// @block 3.1 Shared section header
function SectionHeader({ title, count }: { title: string; count?: number }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase',
      color: '#9aa1ad', marginBottom: 6,
    }}>
      {title}{count != null ? ` · ${count}` : ''}
    </div>
  );
}

// @block 3.2 Panel
function DetailPanel({ loading, detail, sending, onSend }: DetailProps) {
  const [showEmail, setShowEmail] = useState(false);
  if (loading) {
    return (
      <div style={{ marginTop: 12, padding: 14, background: '#1a1e24',
                    border: '1px solid #242a32', borderRadius: 6, fontSize: 12, opacity: 0.6 }}>
        Loading detail…
      </div>
    );
  }
  if (!detail) {
    return (
      <div style={{ marginTop: 12, padding: 14, background: '#1a1e24',
                    border: '1px solid #242a32', borderRadius: 6, fontSize: 12, opacity: 0.6 }}>
        No detail available for this item.
      </div>
    );
  }

  const draft = detail.email_draft;
  const alreadySent = !!draft?.sent_at;

  return (
    <div style={{ marginTop: 12, padding: 14, background: '#1a1e24',
                  border: '1px solid #242a32', borderRadius: 6, fontSize: 13 }}>
      {/* @subsection 4.2.1 Talking script — the key points for this person (primary). */}
      {detail.talking_points.length > 0 && (
        <>
          <SectionHeader title="What we're trying to do" count={detail.talking_points.length} />
          <ul style={{ margin: '0 0 14px 0', paddingLeft: 18, fontSize: 13, lineHeight: 1.5 }}>
            {detail.talking_points.map((p, i) => (<li key={i} style={{ marginBottom: 4 }}>{p}</li>))}
          </ul>
        </>
      )}

      {detail.first_moves.length > 0 && (
        <>
          <SectionHeader title="Opening moves" count={detail.first_moves.length} />
          <ul style={{ margin: '0 0 14px 0', paddingLeft: 18, fontSize: 13, lineHeight: 1.5 }}>
            {detail.first_moves.map((p, i) => (<li key={i} style={{ marginBottom: 4 }}>{p}</li>))}
          </ul>
        </>
      )}

      {detail.voicemail_script && (
        <>
          <SectionHeader title="Voicemail script" />
          <div style={{
            background: '#16191e', border: '1px solid #2a2f37', borderRadius: 6,
            padding: '10px 12px', fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre-wrap',
            fontFamily: 'inherit',
          }}>
            {detail.voicemail_script}
          </div>
        </>
      )}

      {detail.talking_points.length === 0 && detail.first_moves.length === 0 && !detail.voicemail_script && (
        <div style={{ fontSize: 12, opacity: 0.6, padding: '2px 0' }}>No talking points yet for this firm.</div>
      )}

      {/* @subsection 4.2.2 Pre-drafted email — behind its own button, not shown by default. */}
      <div style={{ marginTop: 14, borderTop: '1px solid #242a32', paddingTop: 12 }}>
        <button
          onClick={() => setShowEmail((s) => !s)}
          style={{ background: 'transparent', color: '#a7d3ff', border: '1px solid #2d5288',
                   borderRadius: 6, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
        >
          {showEmail ? '✕ Hide email' : '✉ Pre-drafted email'}
        </button>
        {alreadySent && !showEmail && (
          <span style={{ marginLeft: 10, fontSize: 12, color: '#a3e4b6' }}>✓ already sent</span>
        )}

        {showEmail && !draft && (
          <div style={{ marginTop: 10, fontSize: 12, opacity: 0.6, padding: 10,
                        background: '#16191e', border: '1px solid #2a2f37', borderRadius: 6 }}>
            No email draft for this item.
          </div>
        )}
        {showEmail && draft && (
          <div style={{ marginTop: 10, background: '#16191e', border: '1px solid #2a2f37',
                        borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ padding: '10px 12px', borderBottom: '1px solid #242a32', fontSize: 12, lineHeight: 1.7 }}>
              <div><span style={{ opacity: 0.5 }}>From:</span> {draft.sender_account ?? '—'}</div>
              <div><span style={{ opacity: 0.5 }}>To:</span> {detail.email}</div>
              <div><span style={{ opacity: 0.5 }}>Subject:</span> <strong>{draft.subject ?? '—'}</strong></div>
            </div>
            <div style={{ padding: '12px', maxHeight: 240, overflowY: 'auto', fontSize: 13, lineHeight: 1.55,
                          background: '#1a1e24', color: '#d6dae0' }}
                 dangerouslySetInnerHTML={{ __html: draft.body_html ?? '' }} />
            <div style={{ padding: '10px 12px', borderTop: '1px solid #242a32',
                          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {alreadySent ? (
                <span style={{ fontSize: 12, color: '#a3e4b6' }}>✓ Sent {new Date(draft.sent_at!).toLocaleString()}</span>
              ) : (
                <>
                  <button onClick={onSend} disabled={sending}
                          style={{ background: '#1e3a5f', color: '#a7d3ff', border: '1px solid #2d5288',
                                   borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600,
                                   cursor: sending ? 'wait' : 'pointer' }}>
                    {sending ? 'Sending…' : '✉ Send via Signal'}
                  </button>
                  <span style={{ fontSize: 11, opacity: 0.6 }}>Sends from {draft.sender_account}.</span>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// @section 4 DispositionRow — inline form revealed after a call (or via "Log").
type DispProps = {
  item: CallQueueItem;
  onSubmit: (d: CallDisposition, notes: string, callbackAt: string) => void;
  onCancel: () => void;
};

function DispositionRow({ item, onSubmit, onCancel }: DispProps) {
  const [disposition, setDisposition] = useState<CallDisposition>('connected');
  const [notes, setNotes] = useState('');
  const [callbackAt, setCallbackAt] = useState('');

  return (
    <div style={{ marginTop: 12, padding: 12, background: '#1a1e24',
                  border: '1px solid #242a32', borderRadius: 6 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        {DISPOSITIONS.map(d => (
          <button
            key={d.key}
            onClick={() => setDisposition(d.key)}
            style={{
              background: disposition === d.key ? '#1e3a5f' : 'transparent',
              color: disposition === d.key ? '#a7d3ff' : '#9aa1ad',
              border: `1px solid ${disposition === d.key ? '#2d5288' : '#2a2f37'}`,
              borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer',
            }}
          >
            {d.label}
          </button>
        ))}
      </div>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder={`Notes for ${fullName(item)}…`}
        rows={2}
        style={{ width: '100%', boxSizing: 'border-box',
                background: '#16191e', color: '#e6e8eb',
                border: '1px solid #2a2f37', borderRadius: 6,
                padding: '8px 10px', fontSize: 13, marginBottom: 10,
                fontFamily: 'inherit', resize: 'vertical' }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12, opacity: 0.7 }}>Callback at:</label>
        <input
          type="datetime-local"
          value={callbackAt}
          onChange={(e) => setCallbackAt(e.target.value)}
          style={{ background: '#16191e', color: '#e6e8eb',
                  border: '1px solid #2a2f37', borderRadius: 6,
                  padding: '4px 8px', fontSize: 12 }}
        />
        <span style={{ flex: 1 }} />
        <button
          onClick={onCancel}
          style={{ background: 'transparent', color: '#9aa1ad',
                  border: '1px solid #2a2f37', borderRadius: 6,
                  padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}
        >
          Cancel
        </button>
        <button
          onClick={() => onSubmit(disposition, notes, callbackAt)}
          style={{ background: '#1e3a5f', color: '#a7d3ff',
                  border: '1px solid #2d5288', borderRadius: 6,
                  padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
        >
          ✓ Done · next
        </button>
      </div>
    </div>
  );
}

// @section 5 CallCardMobile — purpose-built mobile card for one queue item.
// Replaces the desktop 6-col grid on phones: firm + status header, person +
// email, a big tappable phone, and a full-width primary Call button. Same data
// + handlers as the desktop row; the parent picks which to render by viewport.
type CallCardMobileProps = {
  item: CallQueueItem;
  eff: { disposition: CallDisposition | null; called_at: string | null; callback_at: string | null; attempts_total: number };
  phone: string | null;
  isBusy: boolean;
  detailOpen: boolean;
  dispOpen: boolean;
  onCall: () => void;
  onToggleDetail: () => void;
  onToggleDisp: () => void;
};

function CallCardMobile({ item, eff, phone, isBusy, detailOpen, dispOpen, onCall, onToggleDetail, onToggleDisp }: CallCardMobileProps) {
  const d = eff.disposition;
  const pillBg =
    d === 'connected' ? '#143a1f' :
    d === 'voicemail' ? '#3a2e14' :
    d === 'bad_number' || d === 'wrong_person' ? '#3a1414' :
    d ? '#1f2329' : 'transparent';
  const pillColor =
    d === 'connected' ? '#a3e4b6' :
    d === 'voicemail' ? '#e4ce8b' :
    d === 'bad_number' || d === 'wrong_person' ? '#e88a8a' :
    d ? '#9aa1ad' : '#5a6068';
  return (
    <div>
      {/* Header — firm (tap for detail) + disposition */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div onClick={onToggleDetail} style={{ cursor: 'pointer', minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 11, opacity: 0.5, flexShrink: 0 }}>#{item.rank}</span>
            <span style={{ fontWeight: 600, fontSize: 15, color: detailOpen ? '#a7d3ff' : 'inherit' }}>
              {item.firm_name ?? item.domain}
            </span>
          </div>
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
            {[item.city, item.state].filter(Boolean).join(', ')}
            {item.gmaps_rating != null && (
              <> · ★ {item.gmaps_rating}{item.gmaps_review_count ? ` (${item.gmaps_review_count})` : ''}</>
            )}
          </div>
        </div>
        <span style={{ flexShrink: 0, display: 'inline-block', padding: '2px 8px', borderRadius: 999,
                       fontSize: 11, border: '1px solid #2a2f37', background: pillBg, color: pillColor }}>
          {d ?? 'not called'}
        </span>
      </div>

      {/* Person + email */}
      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 14 }}>{fullName(item)}{item.title ? ` · ${item.title}` : ''}</div>
        <a href={`mailto:${item.email}`} style={{ fontSize: 12, color: '#9aa1ad', textDecoration: 'none', wordBreak: 'break-all' }}>
          {item.email}
        </a>
      </div>

      {/* Phone — big + tappable */}
      <div style={{ marginTop: 8 }}>
        {phone ? (
          <a href={`tel:${phone}`} style={{ fontSize: 16, fontWeight: 600, color: '#a7d3ff', textDecoration: 'none' }}>
            {formatPhone(item.phone)}
          </a>
        ) : (
          <span style={{ fontSize: 14, opacity: 0.4 }}>no phone</span>
        )}
        {item.touches.length > 0 && (
          <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
            {item.touches.length} email{item.touches.length === 1 ? '' : 's'} sent
            {item.touches[item.touches.length - 1]?.sent_at && (<> · last {fmtAt(item.touches[item.touches.length - 1]?.sent_at)}</>)}
          </div>
        )}
        {eff.attempts_total > 0 && (
          <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
            {eff.attempts_total} attempt{eff.attempts_total === 1 ? '' : 's'}
            {eff.called_at && ` · ${fmtAt(eff.called_at)}`}
            {eff.callback_at && (<> · callback {fmtAt(eff.callback_at)}</>)}
          </div>
        )}
      </div>

      {/* Actions — Call is the full-width primary action */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <button
          onClick={onCall}
          disabled={!phone || isBusy}
          style={{
            flex: '1 1 100%', background: phone ? '#1e3a5f' : '#1f2329',
            color: phone ? '#a7d3ff' : '#5a6068', border: `1px solid ${phone ? '#2d5288' : '#2a2f37'}`,
            borderRadius: 8, padding: '13px', fontSize: 15, fontWeight: 600,
            cursor: phone ? 'pointer' : 'not-allowed',
          }}
        >
          {isBusy ? '…' : '📞 Call'}
        </button>
        <button
          onClick={onToggleDisp}
          style={{ flex: 1, background: 'transparent', color: '#9aa1ad', border: '1px solid #2a2f37',
                   borderRadius: 8, padding: '11px', fontSize: 13, cursor: 'pointer' }}
        >
          {dispOpen ? 'Hide' : 'Log outcome'}
        </button>
        {item.html_path && (
          <a href={item.html_path} target="_blank" rel="noopener noreferrer"
             style={{ flex: 1, textAlign: 'center', background: 'transparent', color: '#9aa1ad',
                      border: '1px solid #2a2f37', borderRadius: 8, padding: '11px', fontSize: 13, textDecoration: 'none' }}>
            Teardown
          </a>
        )}
      </div>
    </div>
  );
}
